/**
 * Builds and fires the bounded operational event for a renderer Remix parity divergence.
 *
 * Every attribute is drawn from a closed union, so the payload carries no flow identifier, no
 * content reference, and no user content.
 *
 * `treatmentPhase` records the migration phase the event was observed in. Diagnostics only run
 * under `isMachineReadTreatment`, so today the only value is `machine-read`; it exists to keep
 * events from this phase distinguishable once the authority cutover (CCCEMAU-3480) changes what the
 * renderer reads.
 */
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import type {
	RemixParityDurationBucket,
	RemixParityOutcome,
	RemixParityReason,
} from './remixParityObservability.ts';
import type {
	RendererRemixDivergence,
	RendererRemixSevereDivergence,
} from './rendererRemixParity.ts';

export const createDivergenceSignature = ({
	legacyPhase,
	machinePhase,
	comparisonPoint,
	divergenceReason,
}: RendererRemixDivergence): string =>
	`${legacyPhase}|${machinePhase}|${comparisonPoint}|${divergenceReason}`;

export const dispatchRendererRemixParityEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ legacyPhase, machinePhase, comparisonPoint, divergenceReason }: RendererRemixSevereDivergence,
): void => {
	try {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'diverged',
				actionSubject: 'rendererRemixStateParity',
				source: 'rendererRemix',
				tags: ['remix', 'stateRearchitecture'],
				attributes: {
					entryPoint: 'renderer',
					severity: 'critical',
					outcome:
						divergenceReason === 'terminal-outcome-mismatch'
							? 'terminal-mismatch'
							: 'released-unresolved',
					reason: divergenceReason,
					legacyPhase,
					machinePhase,
					comparisonPoint,
					divergenceReason,
					treatmentPhase: 'machine-read',
				},
			},
		}).fire();
	} catch {
		// Diagnostics must never affect the renderer flow.
	}
};

export type RendererRemixParityMechanic = Readonly<{
	legacyPhase: RendererRemixDivergence['legacyPhase'];
	machinePhase: RendererRemixDivergence['machinePhase'];
	comparisonPoint: RendererRemixDivergence['comparisonPoint'];
	action: 'observed' | 'resolved';
	outcome: Extract<RemixParityOutcome, 'timing-skew' | 'converged'>;
	reason: Extract<RemixParityReason, 'awaiting-convergence' | 'phase-converged'>;
	durationBucket: RemixParityDurationBucket;
}>;

/** Emits low-severity timing mechanics under a distinct dashboard subject. */
export const dispatchRendererRemixParityMechanicEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		legacyPhase,
		machinePhase,
		comparisonPoint,
		action,
		outcome,
		reason,
		durationBucket,
	}: RendererRemixParityMechanic,
): void => {
	try {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action,
				actionSubject: 'rendererRemixParityMechanic',
				source: 'rendererRemix',
				tags: ['remix', 'stateRearchitecture'],
				attributes: {
					entryPoint: 'renderer',
					severity: 'info',
					outcome,
					reason,
					legacyPhase,
					machinePhase,
					comparisonPoint,
					durationBucket,
					treatmentPhase: 'machine-read',
				},
			},
		}).fire();
	} catch {
		// Diagnostics must never affect the renderer flow.
	}
};
